import React from 'react';

import { useParams } from 'react-router-dom';

import { ArchiveIcon, BeakerIcon, FileIcon } from '@primer/octicons-react';

import usePageTitle from '../../../utils/hooks/use_page_title';

import VersionIcon from '../../common/icons/VersionIcon';

import ClipboardLabel from '../../common/ClipboardLabel/ClipboardLabel';
import CollapsibleCardView from '../../common/CardView/CollapsibleCardView';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import HeadingLabel from '../../common/Labels/HeadingLabel';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import SubHeadingLabel from '../../common/Labels/SubHeadingLabel';

import PageNotFound from '../../pages/PageNotFound';

import { useRedirectOnWorkspaceChange } from '../../../utils/hooks/use_redirect_on_value_change';

import PageLayout from '../../pages/PageLayout';
import PrimaryContent from '../../pages/layouts/PrimaryContent';

import { OrchestrationDataProvider, useOrchestrationData } from '../OrchestrationDataContext/OrchestrationDataContext';
import OrchestrationStatusBadge from '../OrchestrationStatusBadge/OrchestrationStatusBadge';
import OrchestrationTimeline from '../OrchestrationTimeline/OrchestrationTimeline';

import { TaskListDataContextProvider, useTaskListData } from '../TaskListContext/TaskListContext';
import NoTasksSlate from '../TaskBlankSlate/TaskBlankSlate';
import TaskListTable from '../TaskListTable/TaskListTable';

const ContextualTaskListTable = () => {
  const { taskListData, loading, error } = useTaskListData();

  return (
    <div className="mt-2 bg-white border border-border-light rounded relative">
      {
        loading && (
          <LoadingSpinner animationKey="task-list-spinner" size="medium" useOverlay />
        )
      }
      {
        error ? (
          <ErrorSlate
            errorText="Something went wrong loading your tasks"
          />
        ) : !taskListData?.length ? (
          <NoTasksSlate />
        ) : (
          <TaskListTable taskListData={taskListData} />
        )
      }
    </div>
  )
}

export const OrchestrationDetailPage = ({
  orchestration,
  loading,
  error,
}) => {
  const routeParams = useParams();

  usePageTitle(`${orchestration?.name ? orchestration.name : 'Workload'} | Wallaroo`);

  const orchName = orchestration?.name ? orchestration.name : '---';
  const orchSha = orchestration?.sha;
  if (!loading && !error && !orchestration) {
    return <PageNotFound orchestrationID={routeParams.id} />;
  }

  return (
    <div className="w-full mb-16">
      <div className="bg-brand border-t-2 border-border-brand text-white flex justify-center items-center">
        <div className="px-16 py-4 w-full flex-grow flex flex-col items-start">
          <div className="flex items-center">
            <Icon icon={BeakerIcon} size={48} />
            <HeadingLabel
              className="ml-2 mt-1"
              size="4xl"
              italic={!orchestration?.name}
              bold={!!orchestration?.name}
            >
              {orchName}
            </HeadingLabel>
          </div>
          <div className="ml-6 my-2 flex items-center">
            <Icon icon={VersionIcon} size={32} useTailwindSizing />
            <SubHeadingLabel className="ml-2" size="2xl">
              {routeParams?.id}
            </SubHeadingLabel>
          </div>
          {
            !orchestration ? (
              error ? (
                <SubHeadingLabel className="ml-2 mt-2" size="xl">
                  Something went wrong loading this workload
                </SubHeadingLabel>
              ) : (
                <div className="ml-2 mt-2 flex flex-row">
                  <SubHeadingLabel size="xl">
                    Loading Workload Data
                  </SubHeadingLabel>
                  <LoadingSpinner className="ml-2" animationKey="orchestration-detail-loading-spinner" />
                </div>
              )
            ) : (
              <>
                <div className="ml-6 mt-1 mb-2 flex-grow flex-wrap flex flex-row items-center justify-center">
                  <div className="flex items-center justify-center">
                    <Icon icon={FileIcon} size={32} />
                    <SubHeadingLabel bold className="ml-2" size="xl">
                      {orchestration.file_name}
                    </SubHeadingLabel>
                  </div>
                  {
                    orchSha ? (
                      <ClipboardLabel
                        className="ml-2"
                        iconSize={12}
                        labelValue={orchSha}
                        displayLabel={orchSha}
                        LabelComponent={SubHeadingLabel}
                        showClipboardIconOnHover
                      />
                    ) : null
                  }
                </div>
                <OrchestrationStatusBadge status={orchestration?.status} />
              </>
            )
          }
        </div>
      </div>

      {!error && (
        <PageLayout>
          <PrimaryContent>
            <div className="mb-2">
              <OrchestrationTimeline
                orchestrationHistory={[orchestration]}
                error={error}
                loading={loading}
                timelineTitle={orchName}
              />
            </div>
            <TaskListDataContextProvider orchSha={orchestration?.sha}>
              <CollapsibleCardView
                headerText={`Workload Log: ${orchName}`}
                icon={ArchiveIcon}
              >
                <ContextualTaskListTable />
              </CollapsibleCardView>
            </TaskListDataContextProvider>
          </PrimaryContent>
        </PageLayout>
      )}
    </div>
  );
};

const ContextualOrchestrationDetailPage = () => {
  const {
    orchestration,
    loading,
    error,
  } = useOrchestrationData();

  useRedirectOnWorkspaceChange('/models')

  return (
    <OrchestrationDataProvider>
      <OrchestrationDetailPage
        orchestration={orchestration}
        loading={loading}
        error={error}
      />
    </OrchestrationDataProvider>
  );
}

const WrappedOrchestrationDetailPage = () => (
  <OrchestrationDataProvider>
    <ContextualOrchestrationDetailPage />
  </OrchestrationDataProvider>
)

export default WrappedOrchestrationDetailPage;
